import React, { useEffect } from 'react'

import { safeHtml } from '@library/dom'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FontFamily,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { BasketFixedBanner as BasketFixedBannerData } from '@library/api-menu-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

type FixedBannerEvent = {
  action: string
  properties: Record<string, unknown>
}

const Label = withCitrusTheme(Box, (theme) => ({
  color: theme.color(Color.Success_900),
  fontFamily: theme.tokens.FontFamilies[FontFamily.Book],
  fontSize: '14px',
  lineHeight: '20px',
  '& strong': {
    fontFamily: theme.tokens.FontFamilies[FontFamily.Bold],
    fontWeight: 700,
  },
}))

export const BasketFixedBanner = ({
  labelHTML,
  color,
  backgroundColor,
  analytics,
}: BasketFixedBannerData) => {
  const analyticsEvent = analytics?.event
  // Serialised so the effect gets a stable dependency when SWR returns a new but equal object
  const analyticsProperties = JSON.stringify(analytics?.properties ?? {})

  useEffect(() => {
    if (analyticsEvent) {
      queueTrackingEvent<FixedBannerEvent>({
        action: analyticsEvent,
        properties: JSON.parse(analyticsProperties),
      })
    }
  }, [analyticsEvent, analyticsProperties])

  // Webclient has no dark mode, so only the light colours from the xAPI apply

  return (
    <Box
      alignItems={AlignItems.Center}
      bg={Color.Success_50}
      data-testid="basket-fixed-banner"
      display={Display.Flex}
      flexShrink={0}
      justifyContent={JustifyContent.Center}
      paddingH={2}
      paddingV={0.5}
      style={{
        backgroundColor: backgroundColor?.light,
        borderRadius: '4px',
      }}
    >
      <Label dangerouslySetInnerHTML={safeHtml(labelHTML)} style={{ color: color?.light }} />
    </Box>
  )
}
